import React from "react";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import { useAuth } from "@/context/auth";
import { createBillingSetupIntent } from "@/queries/api/billingApi";
import { useConfirmBillingSetupMutation } from "@/queries/hooks/billingHooks";
import { QueryKeys } from "@/queries/queryKeys";
import type { BillingStatus } from "@/schemas/billing";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

const log = Logger.scope("Billing");

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> | null {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
  if (!key) return null;
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
}

const APPEARANCE: StripeElementsOptions["appearance"] = {
  theme: "stripe",
  variables: {
    colorPrimary: "#220a0a",
    colorText: "#220a0a",
    borderRadius: "8px",
  },
};

export type PaymentMethodFormProps = {
  /**
   * Where Stripe sends the browser back if the payment method needs a
   * redirect (rare: bank auth fallbacks). The page at this URL must handle the
   * `?setup_intent=` return via {@link useSetupIntentReturn}.
   */
  returnUrl: string;
  /** Called once `billing/confirm-setup` saved the payment method. */
  onSaved?: (status: BillingStatus) => void;
  /**
   * Imperative submit for hosts that own the submit button (the onboarding
   * wizard's Continue). Resolves true when the payment method was saved.
   */
  registerSubmit?: (submit: (() => Promise<boolean>) | null) => void;
  /** When set, the form renders its own submit button with this label. */
  submitLabel?: string;
  /** When set, a Cancel link is rendered next to the submit button. */
  onCancel?: () => void;
};

/**
 * Stripe Payment Element (card + US bank account) backed by a SetupIntent from
 * `POST billing/setup-intent`. Shared by the owner-onboarding billing step,
 * the payment-resolution page and Settings → Billing.
 */
export function PaymentMethodForm(props: PaymentMethodFormProps) {
  const { t, i18n } = useTranslation("billing");
  const { user } = useAuth();
  const stripe = getStripe();

  const intentQuery = useQuery({
    // One SetupIntent per mounted form; never reuse a cached secret.
    queryKey: [QueryKeys.BILLING_SETUP_INTENT, user?.userId ?? ""],
    queryFn: createBillingSetupIntent,
    enabled: Boolean(stripe),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { registerSubmit } = props;
  React.useEffect(() => {
    return () => registerSubmit?.(null);
  }, [registerSubmit]);

  if (!stripe) {
    return (
      <p className="body-small text-red-600" role="alert">
        {t("form.unavailable")}
      </p>
    );
  }
  if (intentQuery.isError) {
    return (
      <div className="flex flex-col gap-3 items-start" role="alert">
        <p className="body-small text-red-600">{t("form.loadFailed")}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => intentQuery.refetch()}
        >
          {t("form.tryAgain")}
        </Button>
      </div>
    );
  }
  const clientSecret = intentQuery.data?.clientSecret;
  if (!clientSecret) {
    return <p className="body-small text-gray-500">{t("form.loading")}</p>;
  }

  return (
    <Elements
      key={clientSecret}
      stripe={stripe}
      options={{
        clientSecret,
        appearance: APPEARANCE,
        locale: i18n.language.startsWith("es") ? "es" : "en",
      }}
    >
      <PaymentMethodFormInner {...props} />
    </Elements>
  );
}

function PaymentMethodFormInner({
  returnUrl,
  onSaved,
  registerSubmit,
  submitLabel,
  onCancel,
}: PaymentMethodFormProps) {
  const { t } = useTranslation("billing");
  const stripe = useStripe();
  const elements = useElements();
  const confirmSetup = useConfirmBillingSetupMutation();
  const [ready, setReady] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const submit = React.useCallback(async (): Promise<boolean> => {
    if (!stripe || !elements || !ready) {
      notify.error(t("form.notReady"));
      return false;
    }
    setBusy(true);
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: { return_url: returnUrl },
      });
      if (result.error) {
        // Validation / decline messages from Stripe are already user-facing.
        notify.error(result.error.message ?? t("form.confirmFailed"));
        return false;
      }
      const intent = result.setupIntent;
      if (
        intent.status === "requires_payment_method" ||
        intent.status === "canceled"
      ) {
        notify.error(t("form.confirmFailed"));
        return false;
      }
      // succeeded / processing / requires_action (ACH micro-deposits): the
      // backend decides what's saved and reports paymentMethodPending.
      const status = await confirmSetup.mutateAsync(intent.id);
      onSaved?.(status);
      return true;
    } catch (e) {
      // API failures are toasted by the global MutationCache handler.
      log.error("payment method setup failed", {
        error: e instanceof Error ? e.message : String(e),
      });
      return false;
    } finally {
      setBusy(false);
    }
  }, [stripe, elements, ready, returnUrl, confirmSetup, onSaved, t]);

  React.useEffect(() => {
    registerSubmit?.(submit);
  }, [registerSubmit, submit]);

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement
        onReady={() => setReady(true)}
        options={{ layout: "tabs" }}
      />
      {submitLabel ? (
        <div className="flex flex-row gap-4 items-center">
          <Button
            type="button"
            variant="primary"
            onClick={() => void submit()}
            disabled={busy || !ready}
          >
            {busy ? t("form.saving") : submitLabel}
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="link"
              onClick={onCancel}
              disabled={busy}
            >
              {t("form.cancel")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const handledSetupIntents = new Set<string>();

/**
 * Handles the return from a Stripe redirect (`?setup_intent=…&redirect_status=…`):
 * confirms the SetupIntent with the backend once, then strips the params from
 * the URL. Mount it on every page that passes its URL as `returnUrl`.
 */
export function useSetupIntentReturn(options: {
  enabled?: boolean;
  onConfirmed?: (status: BillingStatus) => void;
}) {
  const { enabled = true, onConfirmed } = options;
  const { t } = useTranslation("billing");
  const confirmSetup = useConfirmBillingSetupMutation();
  const onConfirmedRef = React.useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;
  const [pending, setPending] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams(window.location.search);
    const setupIntentId = params.get("setup_intent");
    if (!setupIntentId) return;
    const redirectStatus = params.get("redirect_status");

    // Drop Stripe's params so a reload doesn't re-confirm.
    for (const k of [
      "setup_intent",
      "setup_intent_client_secret",
      "redirect_status",
    ]) {
      params.delete(k);
    }
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );

    if (handledSetupIntents.has(setupIntentId)) return;
    handledSetupIntents.add(setupIntentId);

    if (redirectStatus === "failed") {
      notify.error(t("form.confirmFailed"));
      return;
    }
    setPending(true);
    confirmSetup
      .mutateAsync(setupIntentId)
      .then((status) => {
        notify.success(t("form.saved"));
        onConfirmedRef.current?.(status);
      })
      .catch((e: unknown) => {
        log.error("setup intent return confirm failed", {
          error: e instanceof Error ? e.message : String(e),
        });
      })
      .finally(() => setPending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { pending };
}
